import {
  EmployeeCardEmployee,
  EmployeeCardPosition,
  EmployeeCardPositionGroup,
  EmployeeListItem,
} from "~/entity/Employee/model/schema";
import {
  GetEmployeeCardSupplementQuery,
  GetEmployeeListSupplementQuery,
} from "~/schemas/generated/graphql";

export const EMPLOYEE_POSITION_SLOT_BY_NUMBER: Record<number, string> = {
  1: "one",
  2: "two",
};

export const EMPLOYEE_POSITION_NUMBER_BY_SLOT: Record<string, number> = {
  one: 1,
  two: 2,
};

export type EmployeeCardPositionGaps = {
  dataConsentId?: string | number | null;
  dataConsent?: {
    id?: string | number | null;
    name?: string | null;
    link?: string | null;
  } | null;
  emailVerificarion?: boolean | null;
  hireByUser?: EmployeeCardSupplementUser | null;
  lastStatusUpdateByUser?: EmployeeCardSupplementUser | null;
  fireByUser?: EmployeeCardSupplementUser | null;
  graphqlInstanceId?: string | null;
};

export type EmployeeCardGroupGaps = {
  educationTraining?: string | null;
  educationCertificate?: string | null;
  directionId?: string | number | null;
  directionRole?: string[] | null;
};

export type EmployeeCardPositionWithGaps = EmployeeCardPosition &
  EmployeeCardPositionGaps;

export type EmployeeCardPositionGroupWithGaps = Omit<
  EmployeeCardPositionGroup,
  "activePositions" | "inactivePositions"
> &
  EmployeeCardGroupGaps & {
    activePositions: EmployeeCardPositionWithGaps[];
    inactivePositions: EmployeeCardPositionWithGaps[];
  };

export type EmployeeCardEmployeeWithGaps = Omit<
  EmployeeCardEmployee,
  "positions"
> & {
  positions: EmployeeCardPositionGroupWithGaps[];
};

type EmployeeCardSupplementUser = {
  id?: string | number | null;
  login?: string | null;
  email?: string | null;
  phone?: string | null;
  fullname?: string | null;
  role?: string | null;
  roleRu?: string | null;
  Employee?: { id?: string | number | null } | null;
};

type CardSupplementPosition = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<GetEmployeeCardSupplementQuery["EmployeeGetter"]>["get"]
    >["Positions"]
  >[number]
>["ActivePosition"];

const toId = (value: string | number | null | undefined) =>
  value == null || value === "" ? "" : String(value);

export const cardPositionGapKey = (
  positionId: string | number | null | undefined,
  dealerId: string | number | null | undefined,
) => `${toId(positionId)}:${toId(dealerId)}`;

export const getActualEmployeePositions = <T extends EmployeeCardPosition>(
  rows: T[] | null | undefined,
): T[] => {
  const actual = (rows || []).filter((row) => row.actualTo == null);
  const latestByDealer = new Map<number, T>();

  actual
    .slice()
    .sort((left, right) =>
      String(right.lastStatusUpdateAt || "").localeCompare(
        String(left.lastStatusUpdateAt || ""),
      ),
    )
    .forEach((row) => {
      if (!latestByDealer.has(row.dealerId)) {
        latestByDealer.set(row.dealerId, row);
      }
    });

  return Array.from(latestByDealer.values());
};

const collectDealerGaps = (
  positionId: string | number | null | undefined,
  dealers: NonNullable<CardSupplementPosition>["Positions"] | null | undefined,
  target: Map<string, EmployeeCardPositionGaps>,
) => {
  (dealers || []).forEach((dealer) => {
    if (!dealer) {
      return;
    }

    const key = cardPositionGapKey(positionId, dealer.DealerId);
    if (target.has(key)) {
      return;
    }

    target.set(key, {
      dataConsentId: dealer.DataConsentId,
      dataConsent: dealer.DataConsent,
      emailVerificarion: dealer.emailVerificarion,
      hireByUser: dealer.hireByUser,
      lastStatusUpdateByUser: dealer.lastStatusUpdateByUser,
      fireByUser: dealer.fireByUser,
      graphqlInstanceId: dealer.InstanceId,
    });
  });
};

const collectGroupGaps = (
  field: CardSupplementPosition | null | undefined,
  target: Map<string, EmployeeCardGroupGaps>,
) => {
  if (!field?.PositionsId) {
    return;
  }

  const roles: string[] = [];
  (field.Position?.Direction?.role || []).forEach((role) => {
    if (role) {
      roles.push(String(role));
    }
  });

  target.set(toId(field.PositionsId), {
    educationTraining: field.Education?.training,
    educationCertificate: field.Education?.certificate,
    directionId: field.Position?.DirectionId,
    directionRole: roles,
  });
};

export const collectEmployeeCardGaps = (
  supplement?: GetEmployeeCardSupplementQuery | null,
) => {
  const positions = new Map<string, EmployeeCardPositionGaps>();
  const groups = new Map<string, EmployeeCardGroupGaps>();
  const employee = supplement?.EmployeeGetter?.get;

  if (!employee || !("Positions" in employee)) {
    return { positions, groups };
  }

  (employee.Positions || []).forEach((group) => {
    if (!group) {
      return;
    }

    collectGroupGaps(group.ActivePosition, groups);
    collectDealerGaps(
      group.ActivePosition?.PositionsId,
      group.ActivePosition?.Positions,
      positions,
    );

    (group.InactivePositions || []).forEach((inactive) => {
      collectDealerGaps(inactive?.PositionsId, inactive?.Positions, positions);
    });
  });

  return { positions, groups };
};

const mergePositionRow = (
  row: EmployeeCardPosition,
  positionId: number,
  gaps: Map<string, EmployeeCardPositionGaps>,
): EmployeeCardPositionWithGaps => ({
  ...row,
  ...(gaps.get(cardPositionGapKey(positionId, row.dealerId)) || {}),
});

export const mergeEmployeeCardWithGaps = (
  employee: EmployeeCardEmployee,
  supplement?: GetEmployeeCardSupplementQuery | null,
): EmployeeCardEmployeeWithGaps => {
  const gaps = collectEmployeeCardGaps(supplement);

  return {
    ...employee,
    positions: (employee.positions || []).map((group) => {
      const groupGaps = gaps.groups.get(toId(group.positionId)) || {};

      return {
        ...group,
        ...groupGaps,
        activePositions: (group.activePositions || []).map((row) =>
          mergePositionRow(row, group.positionId, gaps.positions),
        ),
        inactivePositions: (group.inactivePositions || []).map((row) =>
          mergePositionRow(row, group.positionId, gaps.positions),
        ),
      };
    }),
  };
};

const employeeListGapKey = (
  employeeId: string | number | null | undefined,
  employeePositionId: string | number | null | undefined,
) => `${toId(employeeId)}:${toId(employeePositionId)}`;

type EmployeeListItemGaps = {
  employeePositionInstanceId?: string | number | null;
  rejectReason?: string | null;
  verifiedInDnm?: boolean | null;
  directionInstanceId?: string | number | null;
  directionRole?: string[] | null;
  positionDirectionId?: string | number | null;
};

const collectEmployeeListGaps = (
  supplement?: GetEmployeeListSupplementQuery | null,
) => {
  const gaps = new Map<string, EmployeeListItemGaps>();
  const items = supplement?.EmployeeGetter?.getList?.items || [];

  items.forEach((item) => {
    if (!item) {
      return;
    }

    const key = employeeListGapKey(
      item.Employee?.id,
      item.EmployeePosition?.id,
    );
    if (!key || key === ":") {
      return;
    }

    const roles: string[] = [];
    (item.Direction?.role || []).forEach((role) => {
      if (role) {
        roles.push(String(role));
      }
    });

    gaps.set(key, {
      employeePositionInstanceId: item.EmployeePosition?.instanceId,
      rejectReason: item.EmployeePosition?.rejectReason,
      verifiedInDnm: item.Employee?.verifiedInDnm,
      directionInstanceId: item.Direction?.instanceId,
      directionRole: roles,
      positionDirectionId: item.Position?.directionId,
    });
  });

  return gaps;
};

export const mergeEmployeeListWithGaps = (
  items: EmployeeListItem[],
  supplement?: GetEmployeeListSupplementQuery | null,
): EmployeeListItem[] => {
  const gaps = collectEmployeeListGaps(supplement);

  return items.map((row) => {
    const gap =
      gaps.get(
        employeeListGapKey(row.employee?.id, row.employeePosition?.id),
      ) || {};

    return {
      ...row,
      employee: {
        ...row.employee,
        verifiedInDnm: gap.verifiedInDnm ?? row.employee?.verifiedInDnm,
      },
      employeePosition: row.employeePosition
        ? {
            ...row.employeePosition,
            instanceId:
              gap.employeePositionInstanceId ?? row.employeePosition.instanceId,
            rejectReason: gap.rejectReason ?? row.employeePosition.rejectReason,
          }
        : row.employeePosition,
      direction: row.direction
        ? {
            ...row.direction,
            instanceId: gap.directionInstanceId ?? row.direction.instanceId,
            role: gap.directionRole ?? row.direction.role,
          }
        : row.direction,
      position: row.position
        ? {
            ...row.position,
            directionId: gap.positionDirectionId ?? row.position.directionId,
          }
        : row.position,
    };
  });
};
